import type { ComponentProps } from 'react';
import type QuestionnaireReview from './QuestionnaireReview';
import type { DesignModuleSpecJson } from '../data/contracts';
import type { progressionTemplate } from './progressionTemplate';
import { countRebaseBindings } from './templateRebase';
import {CURRENT_SPEC_VERSION,specAtLeast,templateNeedsCurrentSpec} from '../data/specVersion';

export type QuestionnaireReviewValue = Parameters<ComponentProps<typeof QuestionnaireReview>['apply']>[0];
export function applyProgressionReview(spec: DesignModuleSpecJson, pageKey: string, value: ReturnType<typeof progressionTemplate>): DesignModuleSpecJson {
  const next = structuredClone(spec);
  const page = (next.pages as {key:string;label?:Record<string,string>;screen?:{blocks:Record<string,any>[]}}[]).find(page=>page.key===pageKey);
  if (!page?.screen) throw new Error('studio.command.targets_required');
  const block = page.screen.blocks.find(block=>block.key==='native-progression');
  if (block) block.template=value.template;
  else page.screen.blocks.push({key:'native-progression',type:'template',box:{x:0,y:0,w:12,h:1},title:page.label,template:value.template});
  const presentation=(next.presentation??{}) as {templateMessages?:Record<string,Record<string,string>>};
  next.presentation={...presentation,templateMessages:{...presentation.templateMessages,...value.messages}};
  next.specVersion=specAtLeast(spec.specVersion,'1.10')||templateNeedsCurrentSpec(value.template)?CURRENT_SPEC_VERSION:'1.9';
  return next;
}
/** One reviewed game for a complete native page. No persistence or per-question operation. */
export function applyQuestionnaireReview(spec: DesignModuleSpecJson, pageKey: string, value: QuestionnaireReviewValue): DesignModuleSpecJson {
  const next = structuredClone(spec);
  const page = (next.pages as { key: string; screen?: { blocks: Record<string, any>[] } }[]).find(page => page.key === pageKey);
  if (!page?.screen) throw new Error('studio.command.targets_required');
  for (const patch of value.formulaPatches ?? []) {
    const entity = next.entities[patch.entity] as { fields: { name: string; computed?: { formula: string } }[] };
    const field = entity?.fields.find(field => field.name === patch.field);
    if (!field?.computed || field.computed.formula !== patch.before || !entity.fields.some(field => field.name === patch.total)) throw new Error('studio.command.stale_version');
    field.computed.formula = patch.formula;
  }
  if (value.recordEntity) {
    if (next.entities[value.recordEntity.code]) throw new Error('studio.command.stale_version');
    next.entities[value.recordEntity.code] = value.recordEntity.entity;
  }
  if (value.recordFields) {
    const entity = next.entities[value.recordFields.entity] as { fields: { name: string }[] };
    if (!entity || value.recordFields.fields.some(field => entity.fields.some(existing => existing.name === field.name))) throw new Error('studio.command.stale_version');
    entity.fields.push(...value.recordFields.fields);
  }
  const block = page.screen.blocks.find(block => block.template);
  if (!block) throw new Error('studio.command.targets_required');
  // QUIZ-NATIVE3 GAP-Q16 : le parcours garde l'empreinte de la capture compilée sur laquelle il est posé ; une recompilation de la
  // même capture le conserve, une mise à niveau la retrouve (origine journalisée, même empreinte de pièce).
  const capture = (block.template as { rebased?: { capture?: string } }).rebased?.capture;
  block.template = { ...value.template, ...(capture ? { rebased: { count: countRebaseBindings(value.template.root), capture } } : {}) };
  const entities=new Set<string>();
  const visit=(item:unknown):void=>{if(Array.isArray(item))item.forEach(visit);else if(item&&typeof item==='object'){for(const [key,child] of Object.entries(item)){if(key==='entity'&&typeof child==='string'&&next.entities[child])entities.add(child);else visit(child);}}};
  visit(value.template);
  Object.assign(page,{entities:[...new Set([...((page as {entities?:string[]}).entities??[]),...entities])]});
  const presentation = (next.presentation ?? {}) as { templateMessages?: Record<string, Record<string, string>> };
  next.presentation = { ...presentation, templateMessages: { ...presentation.templateMessages, ...value.messages } };
  next.specVersion = specAtLeast(spec.specVersion,'1.10')||templateNeedsCurrentSpec(value.template)?CURRENT_SPEC_VERSION:'1.9';
  return next;
}
